import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../components/shared/ToastNotification';

interface DeliveryLog {
    id: string;
    date: string;
    status: string;
    quantity_delivered: number;
    notes: string;
    created_at: string;
}

interface OrderDeliveryManagementProps {
    orderId: string;
    inModal?: boolean;
    onSuccess?: () => void;
}

function OrderDeliveryManagement({ orderId, onSuccess }: OrderDeliveryManagementProps) {
    const { success, error } = useToast();
    const [logs, setLogs] = useState<DeliveryLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        status: 'delivered',
        quantity_delivered: '',
        notes: ''
    });

    useEffect(() => {
        fetchLogs();
    }, [orderId]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/orders/${orderId}/delivery_logs`);
            if (response.success) {
                setLogs(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch delivery logs', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLog = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            // We'll reuse the delivery_logs creation logic. 
            // In a real app, I'd have a dedicated endpoint or ensure delivery_logs controller handles order_id.
            // For now, I'll assume we can post to delivery_logs with an order_id.
            // Wait, I need to check where delivery_logs are created.
            
            const response = await api.post('/delivery_logs', {
                delivery_log: {
                    ...formData,
                    order_id: orderId,
                    // customer_id and product_id might be required by validations
                }
            });

            if (response.success || response.id) {
                success('Delivery recorded successfully');
                setShowForm(false);
                fetchLogs();
                if (onSuccess) onSuccess();
            } else {
                error(response.message || 'Failed to record delivery');
            }
        } catch (err) {
            console.error('Failed to create delivery log', err);
            error('Failed to log delivery. Please ensure all required fields are filled.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading tracking history...</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">Shipment & Delivery Logs</h3>
                {!showForm && (
                    <button
                        onClick={() => setShowForm(true)}
                        className="px-4 py-2 bg-indigo-600 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all"
                    >
                        + Log New Dispatch
                    </button>
                )}
            </div>

            {showForm && (
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleCreateLog} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Dispatch Date</label>
                                <input
                                    type="date"
                                    required
                                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-bold"
                                    value={formData.date}
                                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Status</label>
                                <select
                                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-bold appearance-none"
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="delivered">Delivered Successfully</option>
                                    <option value="skipped">Skipped / Out of Station</option>
                                    <option value="returned">Returned / Recalled</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Internal Notes</label>
                            <textarea
                                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm font-medium h-24"
                                placeholder="e.g. Delivered to Neighbor, Package left at door..."
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={() => setShowForm(false)}
                                className="px-6 py-3 text-slate-500 text-xs font-black uppercase tracking-widest hover:bg-slate-100 rounded-xl transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-8 py-3 bg-slate-900 text-white text-xs font-black uppercase tracking-widest rounded-xl hover:bg-black shadow-xl shadow-slate-200 transition-all disabled:opacity-50"
                            >
                                {submitting ? 'Recording...' : 'Record Dispatch'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="space-y-3">
                {logs.length === 0 ? (
                    <div className="p-12 text-center border-2 border-dashed border-slate-100 rounded-3xl">
                        <div className="text-4xl mb-4">🚚</div>
                        <p className="text-slate-400 text-sm font-medium uppercase tracking-widest">No delivery dispatches recorded yet</p>
                    </div>
                ) : (
                    logs.map((log) => (
                        <div key={log.id} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-center justify-between group hover:border-indigo-100 hover:shadow-sm transition-all">
                            <div className="flex items-center gap-5">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${
                                    log.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : 
                                    log.status === 'returned' ? 'bg-rose-50 text-rose-600' : 'bg-slate-50 text-slate-400'
                                }`}>
                                    {log.status === 'delivered' ? '✅' : log.status === 'returned' ? '🔙' : '📦'}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-black text-slate-900 text-sm uppercase tracking-tight">
                                            {new Date(log.date).toLocaleDateString(undefined, { dateStyle: 'long' })}
                                        </h4>
                                        <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border ${
                                            log.status === 'delivered' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 
                                            log.status === 'returned' ? 'bg-rose-50 text-rose-700 border-rose-100' : 'bg-slate-100 text-slate-500 border-slate-200'
                                        }`}>
                                            {log.status}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 font-medium italic">{log.notes || 'No specific notes recorded'}</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1 opacity-50">Identity</div>
                                <div className="text-[10px] font-mono font-bold text-slate-400">LOG-{log.id.split('-')[0].toUpperCase()}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default OrderDeliveryManagement;
